import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { startProUpgrade } from '@/utils/upgrade';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from './AppShell';

import { API, STATUS_CONFIG } from './tasks/constants';
import { TaskList, CreateTaskModal, TaskDetailModal } from './tasks';
import { emitProgressRefresh } from '@/utils/progressEvents';

function SimpleTasksPage() {
  const { user, organization, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('not_started');
  const [ikSectionFilter, setIkSectionFilter] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Fetch permissions for AppShell
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const response = await axios.get(`${API}/ops/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } });
        setPermissions(response.data);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };
    fetchPermissions();
  }, []);

  // Check for ik_section filter from URL
  useEffect(() => {
    const section = searchParams.get('ik_section');
    if (section) {
      setIkSectionFilter(parseInt(section));
    }
  }, [searchParams]);

  // Clear filter function
  const clearIkFilter = () => {
    setIkSectionFilter(null);
    setSearchParams({});
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/export/handlingsplan?mode=internkontroll`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `handlingsplan-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF lastet ned');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(t('tasks.errors.couldNotExport'));
    } finally {
      setExporting(false);
    }
  };

  const [reanalysisPrompt, setReanalysisPrompt] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalysisResult, setReanalysisResult] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const [justCompletedId, setJustCompletedId] = useState(null);

  // Pick the next recommended task from the current task list (excluding given id)
  const pickNextRecommended = (allTasks, excludeId) => {
    const priorityWeight = { high: 3, medium: 2, low: 1 };
    const candidates = allTasks.filter(t => {
      if (t.id === excludeId) return false;
      if (t.parent_task_id) return false; // only top-level
      if (t.status === 'completed') return false;
      if (t.locked) return false;
      return true;
    });
    if (!candidates.length) return null;
    candidates.sort((a, b) => {
      // in_progress first, then not_started
      const statusA = a.status === 'in_progress' ? 0 : 1;
      const statusB = b.status === 'in_progress' ? 0 : 1;
      if (statusA !== statusB) return statusA - statusB;
      // priority desc
      const prioA = priorityWeight[a.priority] || 0;
      const prioB = priorityWeight[b.priority] || 0;
      if (prioA !== prioB) return prioB - prioA;
      // deadline asc
      const dA = a.deadline ? new Date(a.deadline).getTime() : Infinity;
      const dB = b.deadline ? new Date(b.deadline).getTime() : Infinity;
      return dA - dB;
    });
    return candidates[0];
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.patch(`${API}/ops/tasks/${taskId}/status?new_status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const statusLabels = {
        not_started: t('tasks.reopened') || 'Oppgave gjenåpnet',
        in_progress: t('tasks.started') || 'Oppgave startet',
        completed: t('tasks.completedMsg') || 'Bra jobba – du er ett steg nærmere målet'
      };
      toast.success(statusLabels[newStatus] || 'Status oppdatert', {
        duration: newStatus === 'completed' ? 4500 : 2500,
        icon: newStatus === 'completed' ? '🎯' : undefined,
      });

      // Check for auto re-analysis prompt
      const prompt = response.data?.prompt_reanalysis;
      if (prompt) {
        setReanalysisPrompt(prompt);
      }

      // Notify dashboard widget to refresh levels (task OR subtask status change)
      emitProgressRefresh('task-status-change');

      // For top-level task completions, show "next step" banner (unless re-analysis prompt already fired)
      const completedTask = tasks.find(x => x.id === taskId);
      if (newStatus === 'completed' && !prompt && completedTask && !completedTask.parent_task_id) {
        setJustCompletedId(taskId);
        const next = pickNextRecommended(tasks, taskId);
        if (next) {
          setNextStep(next);
          // Auto-dismiss after 10s if user doesn't interact
          setTimeout(() => setNextStep((cur) => (cur && cur.id === next.id ? null : cur)), 10000);
        }
        // Clear flash animation after 1.6s
        setTimeout(() => setJustCompletedId(null), 1600);
      }

      fetchTasks();
      setSelectedTask(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('tasks.upgrade.required') || 'Oppgrader til Pro for å fullføre denne oppgaven');
      } else {
        toast.error(t('tasks.errors.couldNotUpdateStatus'));
      }
    }
  };

  const runReanalysis = async () => {
    if (!reanalysisPrompt) return;
    setReanalyzing(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const lang = (i18n.language || 'nb').startsWith('en') ? 'en' : 'no';
      const r = await axios.post(
        `${API}/gap-analysis/reanalyze-task/${reanalysisPrompt.parent_task_id}?language=${lang}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setReanalysisResult({
        previous: r.data.previous_status,
        result: r.data.result,
        parent_title: reanalysisPrompt.parent_title,
      });
      setReanalysisPrompt(null);
      fetchTasks();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('tasks.reanalysis.failed'));
    } finally {
      setReanalyzing(false);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Oppgave slettet');
      fetchTasks();
      setSelectedTask(null);
    } catch (error) {
      toast.error(t('tasks.errors.couldNotDelete'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Filter tasks by IK section if filter is active
  const filteredTasks = ikSectionFilter
    ? tasks.filter(t => t.ik_section === ikSectionFilter)
    : tasks;

  // Build subtask index: parent_id -> [subtasks]
  const subtasksByParent = {};
  for (const t of filteredTasks) {
    if (t.parent_task_id) {
      (subtasksByParent[t.parent_task_id] = subtasksByParent[t.parent_task_id] || []).push(t);
    }
  }
  // Only top-level tasks in the main list
  const topLevel = filteredTasks.filter(t => !t.parent_task_id);

  // Group tasks by status
  const notStartedTasks = topLevel.filter(t => t.status === 'not_started' || t.status === 'pending');
  const inProgressTasks = topLevel.filter(t => t.status === 'in_progress');
  const completedTasks = topLevel.filter(t => t.status === 'completed');

  const hasLockedTasks = filteredTasks.some(t => t.locked);

  // Decorate tasks with subtask stats for rendering
  const decorate = (t) => {
    const subs = subtasksByParent[t.id] || [];
    return {
      ...t,
      _subtask_count: subs.length,
      _subtask_completed: subs.filter(s => s.status === 'completed').length,
      _just_completed: t.id === justCompletedId,
    };
  };

  // Top 3 focus tasks (in_progress > not_started, high prio first, earliest deadline)
  const focusTasks = (() => {
    const priorityWeight = { high: 3, medium: 2, low: 1 };
    const now = Date.now();
    const pool = [...inProgressTasks, ...notStartedTasks].filter(t => !t.locked);
    const scored = pool.map(t => {
      const statusScore = t.status === 'in_progress' ? 100 : 50;
      const prioScore = (priorityWeight[t.priority] || 0) * 10;
      const deadlineMs = t.deadline ? new Date(t.deadline).getTime() : now + 1e12;
      const overdueBonus = deadlineMs < now ? 30 : 0;
      const deadlineProximity = Math.max(0, 20 - (deadlineMs - now) / 86400000); // days away
      return { t, score: statusScore + prioScore + overdueBonus + deadlineProximity };
    });
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 3).map(s => decorate(s.t));
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* IK Section Filter Banner */}
        {ikSectionFilter && (
          <div className="mb-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 flex items-center justify-between" data-testid="ik-filter-banner">
            <div className="flex items-center gap-2">
              <span className="text-cyan-400 font-medium">{t('common.filter')}:</span>
              <span className="text-cyan-300">{t(`tasks.ikSection.${ikSectionFilter}`) || `IK §5.${ikSectionFilter}`}</span>
              <span className="text-cyan-400/70 text-sm">({filteredTasks.length} {t('tasks.title').toLowerCase()})</span>
            </div>
            <button
              onClick={clearIkFilter}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center gap-1"
              data-testid="clear-ik-filter"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('tasks.clearFilter')}
            </button>
          </div>
        )}

        {/* Ukens fokus (Top 3 focus tasks) */}
        {focusTasks.length > 0 && !ikSectionFilter && (
          <div className="mb-8 rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 via-slate-900/50 to-blue-500/5 p-5" data-testid="focus-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex w-6 h-6 rounded-full bg-cyan-500/20 items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                    </svg>
                  </span>
                  <h2 className="text-lg font-semibold text-white">{t('tasks.focusTitle')}</h2>
                </div>
                <p className="text-xs text-slate-400 mt-1 ml-8">{t('tasks.focusSubtitle')}</p>
              </div>
              <a
                href="#all-tasks"
                className="text-xs font-medium text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                data-testid="focus-see-all"
              >
                {t('tasks.focusSeeAll')}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {focusTasks.map((ft, idx) => (
                <button
                  key={ft.id}
                  onClick={() => setSelectedTask(ft)}
                  className={`group text-left rounded-xl border p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-cyan-500/10 ${
                    ft._just_completed
                      ? 'border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_30px_rgba(16,185,129,0.4)]'
                      : 'border-white/10 bg-slate-950/60 hover:border-cyan-500/40'
                  }`}
                  data-testid={`focus-card-${idx}`}
                >
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <span className={`text-[10px] font-bold tracking-wider uppercase ${
                      ft.priority === 'high' ? 'text-rose-300' :
                      ft.priority === 'medium' ? 'text-amber-300' :
                      'text-slate-400'
                    }`}>
                      #{idx + 1} · {ft.priority || 'normal'}
                    </span>
                    {ft.is_overdue && (
                      <span className="text-[10px] font-bold uppercase text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">
                        {t('tasks.overdue')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-cyan-100">
                    {ft.title}
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                    {ft.deadline && <span>📅 {formatDate(ft.deadline)}</span>}
                    {ft._subtask_count > 0 && (
                      <span className="text-cyan-300">↳ {ft._subtask_completed}/{ft._subtask_count}</span>
                    )}
                  </div>
                  {ft._subtask_count > 0 && (
                    <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-500"
                        style={{ width: `${Math.round((ft._subtask_completed / ft._subtask_count) * 100)}%` }}
                      />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Page Header */}
        <div id="all-tasks" className="flex items-center justify-between mb-6 scroll-mt-8">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="tasks-page-title">{t('tasks.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">
              {filteredTasks.length} {t('tasks.title').toLowerCase()}{ikSectionFilter ? ` (${t('tasks.filtered')})` : ` ${t('common.total')}`} • {completedTasks.length} {t('common.completed')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPDF}
              disabled={exporting}
              className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="export-pdf-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {exporting ? t('common.loading') : t('common.export')}
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="create-task-btn"
            >
              + {t('tasks.newTask')}
            </button>
          </div>
        </div>

        {/* Upgrade Banner */}
        {hasLockedTasks && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6" data-testid="upgrade-banner">
            <p className="font-medium text-white">{t('tasks.upgrade.title')}</p>
            <p className="text-sm text-slate-400 mt-1 mb-3">
              {t('tasks.upgrade.description')}
            </p>
            <button
              className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="upgrade-to-pro-btn"
              onClick={startProUpgrade}
            >
              {t('tasks.upgrade.button')}
            </button>
          </div>
        )}

        {/* Status Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-800/50 p-1 rounded-lg w-fit" data-testid="status-tabs">
          {[
            { key: 'not_started', label: t('tasks.status.notStarted'), count: notStartedTasks.length },
            { key: 'in_progress', label: t('tasks.status.inProgress'), count: inProgressTasks.length },
            { key: 'completed', label: t('tasks.status.completed'), count: completedTasks.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.key
                  ? 'bg-cyan-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <span className="text-lg">{STATUS_CONFIG[tab.key].icon}</span>
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-gray-100' : 'bg-gray-200'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Task List */}
        <TaskList
          tasks={
            (activeTab === 'not_started' ? notStartedTasks :
             activeTab === 'in_progress' ? inProgressTasks :
             completedTasks).map(decorate)
          }
          status={activeTab}
          onTaskClick={setSelectedTask}
          formatDate={formatDate}
          t={t}
        />
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          subtasks={subtasksByParent[selectedTask.id] || []}
          onClose={() => setSelectedTask(null)}
          onStatusChange={updateTaskStatus}
          onDelete={deleteTask}
          formatDate={formatDate}
          onTaskUpdated={fetchTasks}
        />
      )}

      {/* Create Task Modal */}
      {showCreate && (
        <CreateTaskModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchTasks(); }}
        />
      )}


      {/* Next Step Banner (toast-like bottom slide-in) */}
      {nextStep && (
        <div
          className="fixed bottom-6 right-6 z-[55] w-full max-w-sm animate-[slideInRight_0.3s_ease-out]"
          data-testid="next-step-banner"
        >
          <div className="bg-slate-900 border border-cyan-500/30 rounded-xl shadow-2xl shadow-cyan-500/20 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-b border-cyan-500/20 flex items-center gap-2">
              <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <div>
                <p className="text-xs font-bold text-cyan-300 uppercase tracking-wide">{t('tasks.nextStepTitle')}</p>
                <p className="text-[11px] text-slate-400">{t('tasks.nextStepSubtitle')}</p>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm font-semibold text-white line-clamp-2 mb-1">{nextStep.title}</p>
              <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
                {nextStep.priority && (
                  <span className={`font-semibold uppercase ${
                    nextStep.priority === 'high' ? 'text-rose-300' :
                    nextStep.priority === 'medium' ? 'text-amber-300' :
                    'text-slate-400'
                  }`}>
                    {nextStep.priority}
                  </span>
                )}
                {nextStep.deadline && <span>📅 {formatDate(nextStep.deadline)}</span>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setNextStep(null)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-800 transition-colors"
                  data-testid="next-step-dismiss"
                >
                  {t('tasks.nextStepDismiss')}
                </button>
                <button
                  onClick={() => {
                    const fullTask = tasks.find(x => x.id === nextStep.id) || nextStep;
                    setSelectedTask(fullTask);
                    setNextStep(null);
                  }}
                  className="flex-1 py-2 bg-cyan-500 text-slate-950 rounded-lg text-xs font-semibold hover:bg-cyan-400 shadow-lg shadow-cyan-500/25 transition-colors"
                  data-testid="next-step-open"
                >
                  {t('tasks.nextStepOpen')} →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Auto re-analysis prompt */}
      {reanalysisPrompt && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="reanalysis-prompt">
          <div className="bg-slate-900 border border-cyan-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-white">{t('tasks.reanalysis.promptTitle')}</h2>
                <p className="text-sm text-slate-400 mt-1">
                  {t('tasks.reanalysis.promptBody', { count: reanalysisPrompt.total_subtasks, task: reanalysisPrompt.parent_title })}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setReanalysisPrompt(null)}
                className="flex-1 py-2.5 border border-slate-600 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800"
                data-testid="reanalysis-skip"
              >
                {t('tasks.reanalysis.skip')}
              </button>
              <button
                onClick={runReanalysis}
                disabled={reanalyzing}
                className="flex-1 py-2.5 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 disabled:opacity-40 shadow-lg shadow-cyan-500/25"
                data-testid="reanalysis-confirm"
              >
                {reanalyzing ? t('tasks.reanalysis.running') : t('tasks.reanalysis.run')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-analysis result */}
      {reanalysisResult && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="reanalysis-result">
          <div className={`bg-slate-900 border rounded-2xl p-6 max-w-md w-full ${
            reanalysisResult.result.status === 'met' ? 'border-emerald-500/30' :
            reanalysisResult.result.status === 'partial' ? 'border-amber-500/30' :
            'border-rose-500/30'
          }`}>
            <h2 className="text-lg font-bold text-white mb-1">{t('tasks.reanalysis.resultTitle')}</h2>
            <p className="text-xs text-slate-500 mb-4">{reanalysisResult.parent_title}</p>

            <div className="flex items-center gap-3 mb-4">
              <div className={`text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-300`}>
                {reanalysisResult.previous ? t(`gap.${reanalysisResult.previous}`) : '—'}
              </div>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className={`text-sm font-bold px-3 py-1 rounded ${
                reanalysisResult.result.status === 'met' ? 'bg-emerald-500/20 text-emerald-300' :
                reanalysisResult.result.status === 'partial' ? 'bg-amber-500/20 text-amber-300' :
                'bg-rose-500/20 text-rose-300'
              }`}>
                {reanalysisResult.result.status === 'met' ? '✓' : reanalysisResult.result.status === 'partial' ? '⚠' : '✗'}{' '}
                {t(`gap.${reanalysisResult.result.status}`)} • {reanalysisResult.result.confidence}%
              </div>
            </div>

            {reanalysisResult.result.summary && (
              <p className="text-sm text-slate-300 mb-4">{reanalysisResult.result.summary}</p>
            )}

            {reanalysisResult.result.status === 'met' && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-4">
                <p className="text-sm font-semibold text-emerald-300">🎉 {t('tasks.reanalysis.metCelebration')}</p>
              </div>
            )}

            <button
              onClick={() => setReanalysisResult(null)}
              className="w-full py-2.5 bg-cyan-500 text-slate-950 rounded-lg font-semibold hover:bg-cyan-400"
              data-testid="reanalysis-close"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default SimpleTasksPage;
